import { type FC, useState, useMemo, useCallback, useEffect } from 'react';
import { Modal, Input, Select, Radio, Spin, Button } from 'antd';
import { SearchOutlined } from '@ant-design/icons';

import { useTpoGenConfig } from '../../../context/CustomerConfigContext';
import useDimOptionsData from '../../../hooks/useDimOptionsData';
import useUniversePlans from '../../hooks/queries/useUniversePlans';
import type { CreateReportResult } from '../../types';
import type {
    CreateReportFieldConfig,
    CreateReportDimSelectField,
    CreateReportSelectField,
} from '../../../config/scenarioComparisonConfig';
import { DimSelect } from '../../../components/DimSelect';
import type { UseDimOptionsHook, UseDimOptionsParams } from '../../../components/DimSelect';
import { TPO_CREATE_REPORT_MODAL, tpoOptionTestId, tpoPartTestId } from '../../../config/testIds';

const CREATE_REPORT_CANCEL_BTN = tpoPartTestId(TPO_CREATE_REPORT_MODAL, 'cancel_btn');
const CREATE_REPORT_NEXT_BTN = tpoPartTestId(TPO_CREATE_REPORT_MODAL, 'next_btn');
const CREATE_REPORT_CONFIRM_BTN = tpoPartTestId(TPO_CREATE_REPORT_MODAL, 'confirm_btn');
const CREATE_REPORT_EMPTY = tpoPartTestId(TPO_CREATE_REPORT_MODAL, 'empty');

const NO_LABELS: string[] = [];

/** A `dimSelect` field holds an array when `isMultiSelect`, a bare string otherwise. */
type FieldValue = string | string[] | undefined;

const toLabels = (value: FieldValue): string[] => {
    if (Array.isArray(value)) return value;
    return value ? [value] : NO_LABELS;
};

const toText = (value: FieldValue): string => (Array.isArray(value) ? value.join(', ') : value ?? '');

const hasValue = (value: FieldValue): boolean =>
    Array.isArray(value) ? value.length > 0 : value !== undefined && value !== '';

interface CreateNewReportModalProps {
    open: boolean;
    onCancel: () => void;
    onCreate: (result: CreateReportResult) => void;
}

const useReportDimOptions: UseDimOptionsHook = (params: UseDimOptionsParams) =>
    useDimOptionsData({
        dimension: params.dimension,
        attributeType: params.attributeType,
        selectedDimension: params.selectedDimension,
        enabled: params.enabled ?? true,
        searchString: params.searchString ?? '',
    });

const CreateNewReportModal: FC<CreateNewReportModalProps> = ({
    open,
    onCancel,
    onCreate,
}) => {
    const tpoGenConfig = useTpoGenConfig();
    const { labels, scenarioComparisonConfig } = tpoGenConfig;
    const { createReportForm, reportTypeOptions } = scenarioComparisonConfig;
    const { steps } = createReportForm;
    const modalLabels = labels.comparisonReport.createReportModal;

    const [stepIndex, setStepIndex] = useState(0);
    const [formValues, setFormValues] = useState<Record<string, FieldValue>>({});
    const [planSearch, setPlanSearch] = useState('');

    const currentStep = steps[stepIndex];

    const defaultReportType = reportTypeOptions.find(o => o.default)?.value ?? reportTypeOptions[0]?.value ?? '';

    useEffect(() => {
        if (open) {
            setStepIndex(0);
            setFormValues({ reportType: defaultReportType });
            setPlanSearch('');
        }
    }, [open, defaultReportType]);

    const setFieldValue = useCallback((name: string, value: string | undefined) => {
        setFormValues(prev => ({ ...prev, [name]: value }));
    }, []);

    const dimSelectFields = useMemo(
        () => steps.flatMap(s => s.fields.filter((f): f is CreateReportDimSelectField => f.type === 'dimSelect')),
        [steps],
    );

    const dim1Field = dimSelectFields.find(f => f.dimension === 'dim1');
    const dim2Field = dimSelectFields.find(f => f.dimension === 'dim2');

    const dim1Raw = dim1Field ? formValues[dim1Field.name] : undefined;
    const dim2Raw = dim2Field ? formValues[dim2Field.name] : undefined;

    // Memoised so a single-select field, whose value is a bare string, does not hand a
    // fresh array to the plans query key on every render.
    const dim1Labels = useMemo(() => toLabels(dim1Raw), [dim1Raw]);
    const dim2Labels = useMemo(() => toLabels(dim2Raw), [dim2Raw]);

    const { plans, isPending: isPlansPending } = useUniversePlans({
        dim1Labels,
        dim2Labels,
        enabled: currentStep?.key === 'selectPlan',
    });

    const filteredPlans = useMemo(() => {
        if (!planSearch) return plans;
        const lower = planSearch.toLowerCase();
        return plans.filter((p) => p.plan_name.toLowerCase().includes(lower));
    }, [plans, planSearch]);

    const resolveSelectOptions = useCallback((field: CreateReportSelectField) => {
        if (field.optionsKey === 'reportTypeOptions') {
            return reportTypeOptions.map(opt => ({ label: opt.label, value: opt.value, disabled: opt.disabled }));
        }
        return [];
    }, [reportTypeOptions]);

    const isStepValid = useMemo(() => {
        if (!currentStep) return false;
        return currentStep.fields.filter(f => f.required).every(f => hasValue(formValues[f.name]));
    }, [currentStep, formValues]);

    const isLastStep = stepIndex === steps.length - 1;

    const handleNext = useCallback(() => {
        if (isStepValid && !isLastStep) {
            setStepIndex(prev => prev + 1);
            setPlanSearch('');
        }
    }, [isStepValid, isLastStep]);

    const handleCreate = useCallback(() => {
        if (dim1Labels.length === 0 || dim2Labels.length === 0) return;
        const selectedPlanId = toText(formValues.selectedPlan);
        if (!selectedPlanId) return;
        const plan = plans.find((p) => p.id === selectedPlanId);
        if (!plan) return;
        onCreate({
            formData: {
                reportName: toText(formValues.reportName).trim(),
                dim1_labels: dim1Labels,
                dim2_labels: dim2Labels,
                reportType: toText(formValues.reportType),
            },
            selectedPlan: {
                planId: plan.id,
                planName: plan.plan_name,
                createdBy: plan.created_by,
                year: plan.year,
            },
        });
    }, [formValues, dim1Labels, dim2Labels, plans, onCreate]);

    const handleDimChange = useCallback(
        (fieldName: string, value: string | string[]) => {
            // Narrowing the scope can invalidate whatever a dependent field already holds,
            // so clear the children of the field that changed along with the picked plan.
            const dependentNames = dimSelectFields.filter(f => f.dependsOn === fieldName).map(f => f.name);
            setFormValues(prev => {
                const next: Record<string, FieldValue> = { ...prev, [fieldName]: value, selectedPlan: undefined };
                for (const name of dependentNames) next[name] = undefined;
                return next;
            });
        },
        [dimSelectFields],
    );

    const step2Subtitle = useMemo(() => {
        const dim1Display = dim1Labels.length > 0 ? dim1Labels.join(', ') : `<${dim1Field?.label ?? 'Dim1'}>`;
        const dim2Display = dim2Labels.length > 0 ? dim2Labels.join(', ') : `<${dim2Field?.label ?? 'Dim2'}>`;
        return modalLabels.step2Subtitle(dim1Display, dim2Display);
    }, [dim1Labels, dim2Labels, dim1Field, dim2Field, modalLabels]);

    const buildSelectedDimension = (field: CreateReportDimSelectField) => {
        if (!field.dependsOn) return undefined;
        const parentField = dimSelectFields.find(f => f.name === field.dependsOn);
        if (!parentField) return undefined;
        const parentValue = formValues[parentField.name];
        if (!hasValue(parentValue)) return undefined;
        return {
            value: parentValue as string | string[],
            dimension: parentField.dimension,
            attributeType: parentField.attributeType,
        };
    };

    const renderField = (field: CreateReportFieldConfig) => {
        const fieldTestId = tpoPartTestId(TPO_CREATE_REPORT_MODAL, field.name);

        switch (field.type) {
            case 'input':
                return (
                    <div key={field.name} className="flex flex-col gap-1.5">
                        <label className="text-sm text-[#3F3F46] font-medium">
                            {field.label}
                        </label>
                        <Input
                            placeholder={field.placeholder}
                            value={toText(formValues[field.name])}
                            onChange={(e) => setFieldValue(field.name, e.target.value)}
                            size="middle"
                            data-testid={fieldTestId}
                        />
                    </div>
                );

            case 'dimSelect': {
                const selectedDimension = buildSelectedDimension(field);
                return (
                    <div key={field.name} className="flex flex-col gap-1.5">
                        <label className="text-sm text-[#3F3F46] font-medium">
                            {field.label}
                            {field.required ? <span className="text-red-500 ml-0.5">*</span> : null}
                        </label>
                        <DimSelect
                            dimension={field.dimension}
                            attributeType={field.attributeType}
                            isMultiSelect={field.isMultiSelect}
                            placeholder={modalLabels.selectPlaceholder(field.label)}
                            selectedDimension={selectedDimension}
                            useDimOptions={useReportDimOptions}
                            enabled={open}
                            value={field.isMultiSelect ? toLabels(formValues[field.name]) : toText(formValues[field.name])}
                            onChange={(value) => handleDimChange(field.name, value)}
                            testId={fieldTestId}
                        />
                    </div>
                );
            }

            case 'select': {
                const options = resolveSelectOptions(field).map((option) => ({
                    ...option,
                    'data-testid': tpoOptionTestId(fieldTestId, option.value),
                }));
                return (
                    <div key={field.name} className="flex flex-col gap-1.5">
                        <label className="text-sm text-[#3F3F46] font-medium">
                            {field.label}
                        </label>
                        <Select
                            value={toText(formValues[field.name]) || undefined}
                            onChange={(value) => setFieldValue(field.name, value)}
                            options={options}
                            disabled={options.length <= 1}
                            size="middle"
                            data-testid={fieldTestId}
                        />
                    </div>
                );
            }

            case 'planPicker':
                return (
                    <div key={field.name} className="flex flex-col gap-3">
                        <Input
                            placeholder={modalLabels.search}
                            suffix={<SearchOutlined className="text-[#A1A1AA]" />}
                            value={planSearch}
                            onChange={(e) => setPlanSearch(e.target.value)}
                            allowClear
                            data-testid={tpoPartTestId(fieldTestId, 'search_input')}
                        />
                        <Spin spinning={isPlansPending}>
                            <Radio.Group
                                value={toText(formValues[field.name]) || undefined}
                                onChange={(e) => setFieldValue(field.name, e.target.value)}
                                className="w-full"
                            >
                                <div className="flex flex-col gap-2 max-h-[320px] overflow-y-auto">
                                    {filteredPlans.map((plan) => (
                                        <div
                                            key={plan.id}
                                            className={`flex items-center px-4 py-3 border rounded-lg cursor-pointer transition-colors ${
                                                plan.id === toText(formValues[field.name])
                                                    ? 'bg-[#F7F5FF] border-[#44338A]'
                                                    : 'border-[#E4E4E7] bg-white hover:bg-[#FAFAFA]'
                                            }`}
                                            onClick={() => setFieldValue(field.name, plan.id)}
                                            data-testid={tpoOptionTestId(fieldTestId, plan.id)}
                                        >
                                            <Radio value={plan.id} className="!mr-3" />
                                            <div className="flex flex-col">
                                                <span className="text-sm font-medium text-[#18181B]">
                                                    {plan.plan_name}
                                                </span>
                                                {(plan.created_by || plan.year) ? (
                                                    <span className="text-xs text-[#A1A1AA]">
                                                        {modalLabels.createdByLabel(
                                                            plan.created_by ?? '',
                                                            String(plan.year ?? ''),
                                                        )}
                                                    </span>
                                                ) : null}
                                            </div>
                                        </div>
                                    ))}
                                    {!isPlansPending && filteredPlans.length === 0 ? (
                                        <div
                                            className="py-8 text-center text-sm text-[#A1A1AA]"
                                            data-testid={CREATE_REPORT_EMPTY}
                                        >
                                            {labels.common.noData}
                                        </div>
                                    ) : null}
                                </div>
                            </Radio.Group>
                        </Spin>
                    </div>
                );

            default:
                return null;
        }
    };

    const stepIndicator = (
        <div className="flex items-center gap-3 mb-1">
            {steps.map((s, i) => (
                <div key={s.key} className={`h-0.5 flex-1 ${i === stepIndex ? 'bg-[#44338A]' : 'bg-[#E4E4E7]'}`} />
            ))}
        </div>
    );

    const footer = (
        <div className="flex items-center justify-end gap-2">
            <Button onClick={onCancel} data-testid={CREATE_REPORT_CANCEL_BTN}>
                {labels.common.cancel}
            </Button>
            {isLastStep ? (
                <Button
                    type="primary"
                    onClick={handleCreate}
                    disabled={!isStepValid}
                    data-testid={CREATE_REPORT_CONFIRM_BTN}
                >
                    {modalLabels.create}
                </Button>
            ) : (
                <Button
                    type="primary"
                    onClick={handleNext}
                    disabled={!isStepValid}
                    data-testid={CREATE_REPORT_NEXT_BTN}
                >
                    {modalLabels.next}
                </Button>
            )}
        </div>
    );

    const modalTitle = (
        <div className="flex flex-col gap-2">
            <span className="text-base font-semibold">{modalLabels.title}</span>
            <span className="text-sm font-normal text-[#71717A]">
                {stepIndex === 0 ? modalLabels.step1Subtitle : step2Subtitle}
            </span>
            {stepIndicator}
        </div>
    );

    return (
        <Modal
            title={modalTitle}
            open={open}
            footer={footer}
            width={720}
            closable={false}
            maskClosable={false}
            keyboard={false}
            destroyOnClose
        >
            <div className="flex flex-col gap-4" data-testid={TPO_CREATE_REPORT_MODAL} data-state-step={currentStep?.key}>
                {currentStep?.fields.map(renderField)}
            </div>
        </Modal>
    );
};

CreateNewReportModal.displayName = 'CreateNewReportModal';
export default CreateNewReportModal;
